import React from 'react';
import {useForm, SubmitHandler, Resolver, FieldErrors} from 'react-hook-form';
import {olzApi, OlzMetaData, OlzTerminLabelData, OlzTerminTemplateData} from '../../../Api/client';
import {initOlzEditModal, MARKDOWN_NOTICE, OlzEditModal, OlzEditModalStatus} from '../../../Common/Components/OlzEditModal/OlzEditModal';
import {OlzEntityField} from '../../../Common/Components/OlzEntityField/OlzEntityField';
import {OlzTextField} from '../../../Common/Components/OlzTextField/OlzTextField';
import {OlzMultiFileField} from '../../../Components/Upload/OlzMultiFileField/OlzMultiFileField';
import {OlzMultiImageField} from '../../../Components/Upload/OlzMultiImageField/OlzMultiImageField';
import {OlzTimeIntervalField} from '../../../Common/Components/OlzTimeIntervalField/OlzTimeIntervalField';
import {getApiBoolean, getApiNumber, getApiString, getFormBoolean, getFormNumber, getFormString, getResolverResult, validateIntegerOrNull, validateTimeOrNull} from '../../../Utils/formUtils';
import {Entity, isDefined, assert} from '../../../Utils/generalUtils';

import './OlzEditTerminTemplateModal.scss';

interface OlzEditTerminTemplateForm {
    startTime: string;
    durationSeconds: string;
    title: string;
    text: string;
    locationId: number | null;
    organizerUserId: number | null;
    deadlineEarlierSeconds: string;
    deadlineTime: string;
    shouldPromote: string;
    types: (string | boolean)[];
    imageIds: string[];
    fileIds: string[];
    hasNewsletter: string | boolean;
}

const resolver: Resolver<OlzEditTerminTemplateForm> = async (values) => {
    const errors: FieldErrors<OlzEditTerminTemplateForm> = {};
    [errors.startTime, values.startTime] = validateTimeOrNull(values.startTime);
    errors.durationSeconds = validateIntegerOrNull(values.durationSeconds);
    errors.deadlineEarlierSeconds = validateIntegerOrNull(values.deadlineEarlierSeconds);
    [errors.deadlineTime, values.deadlineTime] = validateTimeOrNull(values.deadlineTime);
    return getResolverResult(errors, values);
};

function getFormFromApi(labels: Entity<OlzTerminLabelData>[], apiData?: OlzTerminTemplateData): OlzEditTerminTemplateForm {
    const typesSet = new Set(apiData?.types ?? []);
    return {
        startTime: getFormString(apiData?.startTime),
        durationSeconds: getFormNumber(apiData?.durationSeconds),
        title: getFormString(apiData?.title),
        text: getFormString(apiData?.text),
        locationId: apiData?.locationId ?? null,
        organizerUserId: apiData?.organizerUserId ?? null,
        deadlineEarlierSeconds: getFormNumber(apiData?.deadlineEarlierSeconds),
        deadlineTime: getFormString(apiData?.deadlineTime),
        shouldPromote: getFormBoolean(apiData?.shouldPromote),
        types: labels.map((label) => getFormBoolean(typesSet.has(label.data.ident))),
        fileIds: apiData?.fileIds ?? [],
        imageIds: apiData?.imageIds ?? [],
        hasNewsletter: getFormBoolean(apiData?.newsletter),
    };
}

function getApiFromForm(labels: Entity<OlzTerminLabelData>[], formData: OlzEditTerminTemplateForm): OlzTerminTemplateData {
    const typesSet = new Set(labels
        .map((label, index) => (
            getApiBoolean(formData.types[index]) ? label.data.ident : undefined
        ))
        .filter(isDefined));
    return {
        startTime: getApiString(formData.startTime),
        durationSeconds: getApiNumber(formData.durationSeconds),
        title: getApiString(formData.title) ?? '',
        text: getApiString(formData.text) ?? '',
        locationId: formData.locationId,
        organizerUserId: formData.organizerUserId,
        deadlineEarlierSeconds: getApiNumber(formData.deadlineEarlierSeconds),
        deadlineTime: getApiString(formData.deadlineTime),
        shouldPromote: getApiBoolean(formData.shouldPromote),
        types: Array.from(typesSet),
        fileIds: formData.fileIds,
        imageIds: formData.imageIds,
        newsletter: getApiBoolean(formData.hasNewsletter),
    };
}

// ---

interface OlzEditTerminTemplateModalProps {
    id?: number;
    labels: Entity<OlzTerminLabelData>[];
    meta?: OlzMetaData;
    data?: OlzTerminTemplateData;
}

export const OlzEditTerminTemplateModal = (props: OlzEditTerminTemplateModalProps): React.ReactElement => {
    const {register, handleSubmit, formState: {errors}, control, watch} = useForm<OlzEditTerminTemplateForm>({
        resolver,
        defaultValues: getFormFromApi(props.labels, props.data),
    });

    const [status, setStatus] = React.useState<OlzEditModalStatus>({id: 'IDLE'});
    const [isLocationLoading, setIsLocationLoading] = React.useState<boolean>(false);
    const [isOrganizerLoading, setIsOrganizerLoading] = React.useState<boolean>(false);
    const [isImagesLoading, setIsImagesLoading] = React.useState<boolean>(false);
    const [isFilesLoading, setIsFilesLoading] = React.useState<boolean>(false);

    const imageIds = watch('imageIds');

    const onSubmit: SubmitHandler<OlzEditTerminTemplateForm> = async (values) => {
        setStatus({id: 'SUBMITTING'});
        const meta: OlzMetaData = props?.meta ?? {
            ownerUserId: null,
            ownerRoleId: null,
            onOff: true,
        };
        const data = getApiFromForm(props.labels, values);

        const [err, response] = await (props.id
            ? olzApi.getResult('updateTerminTemplate', {id: props.id, meta, data})
            : olzApi.getResult('createTerminTemplate', {meta, data}));
        if (err) {
            setStatus({id: 'SUBMIT_FAILED', message: `Anfrage fehlgeschlagen: ${JSON.stringify(err || response)}`});
            return;
        }
        setStatus({id: 'SUBMITTED'});
        // This could probably be done more smoothly!
        window.location.reload();
    };

    const onDelete = props.id ? async () => {
        setStatus({id: 'DELETING'});
        const [err, response] = await olzApi.getResult('deleteTerminTemplate', {id: assert(props.id)});
        if (err) {
            setStatus({id: 'DELETE_FAILED', message: `Löschen fehlgeschlagen: ${JSON.stringify(err || response)}`});
            return;
        }
        setStatus({id: 'DELETED'});
        // This could probably be done more smoothly!
        window.location.reload();
    } : undefined;

    const dialogTitle = (props.id === undefined
        ? 'Termin-Vorlage erstellen'
        : 'Termin-Vorlage bearbeiten'
    );
    const isShouldPromoteEnabled = imageIds.length > 0;
    const isLoading = isLocationLoading || isOrganizerLoading || isImagesLoading || isFilesLoading;
    const editModalStatus: OlzEditModalStatus = isLoading ? {id: 'LOADING'} : status;

    return (
        <OlzEditModal
            modalId='edit-termin-template-modal'
            dialogTitle={dialogTitle}
            status={editModalStatus}
            onSubmit={handleSubmit(onSubmit)}
            onDelete={onDelete}
        >
            <div className='row'>
                <div className='col mb-3'>
                    <OlzTextField
                        title='Beginn Zeit'
                        name='startTime'
                        errors={errors}
                        register={register}
                    />
                </div>
                <div className='col mb-3'>
                    <OlzTimeIntervalField
                        title='Dauer'
                        name='durationSeconds'
                        errors={errors}
                        control={control}
                    />
                </div>
            </div>
            <div className='mb-3'>
                <OlzTextField
                    title='Titel'
                    name='title'
                    errors={errors}
                    register={register}
                />
            </div>
            <div className='mb-3'>
                <OlzTextField
                    mode='textarea'
                    title={<>Text {MARKDOWN_NOTICE}</>}
                    name='text'
                    errors={errors}
                    register={register}
                />
            </div>
            <div className='row'>
                <div className='col mb-3'>
                    <OlzEntityField
                        title='Ort'
                        entityType='TerminLocation'
                        name='locationId'
                        errors={errors}
                        control={control}
                        setIsLoading={setIsLocationLoading}
                        nullLabel={'Kein Standart-Termin-Ort'}
                    />
                </div>
                <div className='col mb-3'>
                    <OlzEntityField
                        title='Organisator'
                        entityType='User'
                        name='organizerUserId'
                        errors={errors}
                        control={control}
                        setIsLoading={setIsOrganizerLoading}
                        nullLabel={'Kein Standart-Organisator'}
                    />
                </div>
            </div>
            <div className='row'>
                <div className='col mb-3'>
                    <OlzTimeIntervalField
                        title='Meldeschluss ... vorher (leer: kein Meldeschluss)'
                        name='deadlineEarlierSeconds'
                        errors={errors}
                        control={control}
                    />
                </div>
                <div className='col mb-3'>
                    <OlzTextField
                        title='Meldeschluss Zeit'
                        name='deadlineTime'
                        errors={errors}
                        register={register}
                    />
                </div>
                <div className='col mb-3 shouldPromote-container'>
                    <input
                        type='checkbox'
                        value='yes'
                        {...register('shouldPromote')}
                        disabled={!isShouldPromoteEnabled}
                        id='shouldPromote-input'
                    />
                    <label htmlFor='shouldPromote-input'>
                        Termin-Meldeschluss sofort auf der Startseite anzeigen
                        {isShouldPromoteEnabled ? '' : ' (zuerst Bilder hinzufügen!)'}
                    </label>
                </div>
            </div>
            <div className='mb-3'>
                <label htmlFor='types-container'>Typ</label>
                <div id='types-container'>
                    {props.labels?.map((label, index) => (
                        <span className='types-option' key={`${index}-${label.data.ident}`}>
                            <input
                                type='checkbox'
                                value='yes'
                                {...register(`types.${index}`)}
                                id={`types-${label.data.ident}-input`}
                                key={label.id}
                            />
                            <label htmlFor={`types-${label.data.ident}-input`}>
                                {label.data.name}
                            </label>
                        </span>
                    ))}
                </div>
            </div>
            <div className='mb-3' id='images-upload'>
                <OlzMultiImageField
                    title='Bilder'
                    name='imageIds'
                    errors={errors}
                    control={control}
                    setIsLoading={setIsImagesLoading}
                />
            </div>
            <div className='mb-3' id='files-upload'>
                <OlzMultiFileField
                    title='Dateien'
                    name='fileIds'
                    errors={errors}
                    control={control}
                    setIsLoading={setIsFilesLoading}
                />
            </div>
            <div className='hasNewsletter-container'>
                <input
                    type='checkbox'
                    value='yes'
                    {...register('hasNewsletter')}
                    id='hasNewsletter-input'
                />
                <label htmlFor='hasNewsletter-input'>
                    Newsletter für Änderung
                </label>
            </div>
        </OlzEditModal>
    );
};

export function initOlzEditTerminTemplateModal(
    id?: number,
    meta?: OlzMetaData,
    data?: OlzTerminTemplateData,
): boolean {
    olzApi.call('listTerminLabels', {}).then((response) => {
        initOlzEditModal('edit-termin-template-modal', () => (
            <OlzEditTerminTemplateModal
                id={id}
                labels={response.items}
                meta={meta}
                data={data}
            />
        ));
    });
    return false;
}
